import React, { useCallback, useState } from "react";
import {
    chatSearchColleges,
    saveCollegeToMyList,
    type CollegeWizardResult,
} from "../../services/collegeWizardApi";
import "../../styles/college-wizard.css";
import "../../styles/college-chat-search.css";

interface CollegeChatSearchProps {
    athleteUsername: string;
    onCollegeSaved?: () => void;
}

function divisionColor(division: string): string {
    if (division === "NCAA_D1") return "cw-badge d1";
    if (division === "NCAA_D2") return "cw-badge d2";
    if (division === "NCAA_D3") return "cw-badge d3";
    if (division === "NAIA") return "cw-badge naia";
    return "cw-badge njcaa";
}

function looksTechnical(text: string): boolean {
    return /exception|sql|stack|at com\.|java\./i.test(text);
}

function chatSearchErrorMessage(error: { status?: number; message?: string; body?: { detail?: string } }): string {
    const detail = typeof error?.body?.detail === "string" ? error.body.detail.trim() : "";
    if (detail && !looksTechnical(detail)) {
        return detail;
    }
    const message = typeof error?.message === "string" ? error.message.trim() : "";
    if (message && !looksTechnical(message) && [400, 404, 429, 503].includes(error?.status ?? 0)) {
        return message;
    }
    if (error?.status === 400) {
        return "Enter what you are looking for in a college.";
    }
    if (error?.status === 429) {
        return "You've reached the college search limit for now. Please try again later.";
    }
    if (error?.status === 503) {
        return "College search is temporarily unavailable. Please try again.";
    }
    return "We could not search colleges. Please try again.";
}

export default function CollegeChatSearch({ athleteUsername, onCollegeSaved }: CollegeChatSearchProps) {
    const [message, setMessage] = useState("");
    const [activeQuery, setActiveQuery] = useState("");
    const [priorQuery, setPriorQuery] = useState<string | undefined>(undefined);
    const [results, setResults] = useState<CollegeWizardResult[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [savingId, setSavingId] = useState("");
    const [error, setError] = useState("");

    const handleMessageChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    }, []);

    const runSearch = useCallback(async (query: string, nextPage: number, prior?: string) => {
        const trimmed = query.trim();
        if (!trimmed) {
            setError("Enter what you are looking for in a college.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await chatSearchColleges(trimmed, prior, nextPage, 20);
            setResults(Array.isArray(response.content) ? response.content : []);
            setPage(response.number ?? nextPage);
            setTotalPages(response.totalPages ?? 0);
            setTotalElements(response.totalElements ?? 0);
            setHasSearched(true);
        } catch (cause) {
            setError(chatSearchErrorMessage(cause as { status?: number; message?: string; body?: { detail?: string } }));
            if (nextPage === 0) {
                setResults([]);
                setTotalPages(0);
                setTotalElements(0);
                setHasSearched(true);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const nextQuery = message.trim();
        const nextPrior = activeQuery && activeQuery !== nextQuery ? activeQuery : undefined;
        setActiveQuery(nextQuery);
        setPriorQuery(nextPrior);
        void runSearch(nextQuery, 0, nextPrior);
    }, [activeQuery, message, runSearch]);

    const handlePreviousPage = useCallback(() => {
        if (page <= 0 || loading) {
            return;
        }
        void runSearch(activeQuery, page - 1, priorQuery);
    }, [activeQuery, loading, page, priorQuery, runSearch]);

    const handleNextPage = useCallback(() => {
        if (page >= totalPages - 1 || loading) {
            return;
        }
        void runSearch(activeQuery, page + 1, priorQuery);
    }, [activeQuery, loading, page, priorQuery, runSearch, totalPages]);

    const saveCollege = useCallback(async (college: CollegeWizardResult) => {
        setSavingId(college.ipedsId);
        try {
            await saveCollegeToMyList(athleteUsername, college);
            onCollegeSaved?.();
        } catch {
            setError("We could not save that college to your list. Please try again.");
        } finally {
            setSavingId("");
        }
    }, [athleteUsername, onCollegeSaved]);

    return (
        <div className="ccs-container">
            <div className="ccs-header">
                <h3>Describe the colleges you want</h3>
                <p>Tell us the same things as guided search: sport, division, states, campus setting, school size, and public or private. We will search with those answers.</p>
            </div>
            <form className="ccs-form" onSubmit={handleSubmit}>
                <label htmlFor="college-chat-search-message">College search</label>
                <textarea
                    id="college-chat-search-message"
                    value={message}
                    onChange={handleMessageChange}
                    placeholder="Example: NCAA Division I baseball in Texas at a large public school"
                    maxLength={2000}
                    rows={4}
                    disabled={loading}
                />
                {error ? (
                    <div className="ccs-error" role="alert">{error}</div>
                ) : null}
                <button type="submit" className="ccs-search-btn" disabled={loading || !message.trim()}>
                    {loading ? "Searching..." : "Search colleges"}
                </button>
            </form>

            {loading && results.length === 0 ? (
                <div className="cw-skeleton-grid" aria-busy="true">
                    {Array.from({ length: 6 }).map((_, idx) => (
                        <div key={`chat-skeleton-${idx}`} className="cw-skeleton-card" />
                    ))}
                </div>
            ) : null}

            {hasSearched && !loading && results.length === 0 && !error ? (
                <div className="cw-empty">No colleges found — try a different description</div>
            ) : null}

            {results.length > 0 ? (
                <>
                    {totalElements > 0 ? (
                        <p className="cw-results-summary">{totalElements} colleges found</p>
                    ) : null}
                    <div className={`cw-grid ${loading ? "is-loading" : ""}`}>
                        {results.map((college) => (
                            <div key={college.ipedsId} className="cw-card">
                                <div className="cw-card-title">{college.name}</div>
                                <div>{college.city}, {college.state}</div>
                                <span className={divisionColor(college.division)}>{college.division}</span>
                                <div>Size: {college.size?.toLocaleString() || "—"}</div>
                                <div>Setting: {college.localeType || "—"}</div>
                                <button
                                    type="button"
                                    onClick={() => { void saveCollege(college); }}
                                    disabled={savingId === college.ipedsId}
                                >
                                    {savingId === college.ipedsId ? "Saving..." : "Save to My List"}
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            ) : null}

            {totalPages > 1 ? (
                <div className="cw-actions-row">
                    <button type="button" disabled={page <= 0 || loading} onClick={handlePreviousPage}>Previous</button>
                    <span>Page {page + 1} of {totalPages}</span>
                    <button type="button" disabled={page >= totalPages - 1 || loading} onClick={handleNextPage}>Next</button>
                </div>
            ) : null}
        </div>
    );
}
